/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { Button } from 'sharedUi/Button';
import { valueSetsService } from '../../services/value-sets.service';

/**
 * When a notification fires, as conditions rather than as JSON.
 *
 * The shape is validated on save — `match` must be ALL or ANY, every condition
 * needs a field and an operator, and the tree may not nest too deeply — so this
 * is not free-form and there is no reason to make somebody type it. A malformed
 * tree is also the worst kind of mistake here: it does not error at send, it
 * evaluates to something, and the notification silently goes to everyone or to
 * nobody.
 *
 * Empty means **always fire**, which is the common case and stays one click
 * away rather than requiring `null` to be typed.
 *
 * Nested groups are deliberately not offered. One level of ALL/ANY covers what
 * a trigger condition is for, and the API accepts nesting for anything built by
 * hand or by a migration — this editor simply does not author it.
 */
export interface ConditionNode {
  field?: string;
  operator?: string;
  value?: any;
}
export interface ConditionTree {
  match: 'ALL' | 'ANY';
  conditions: ConditionNode[];
}

interface ConditionTreeEditorProps {
  value: ConditionTree | null;
  onChange: (tree: ConditionTree | null) => void;
  /** Declared parameter names — a condition may only test what the trigger supplies. */
  parameterNames?: string[];
  testId?: string;
}

/**
 * WHICH operators exist is the `conditionOperator` value set's answer, like every
 * other enumeration in this system — so adding one is a seed, not a release.
 *
 * HOW each one's input behaves is not something a value set can express, and
 * that is all this table holds: whether a value is taken at all, and whether it
 * reads as a list or a number. Kept in step with `_condition_holds` in
 * `_4s_sdlc/notification_engine/dispatcher.py`, which is what actually evaluates
 * them.
 *
 * An operator seeded later that is missing here still appears, and takes a plain
 * value — the safe default, since that is what most comparisons are.
 */
interface OperatorBehaviour { label: string; takesValue: boolean; list?: boolean; numeric?: boolean }

const BEHAVIOUR: Record<string, OperatorBehaviour> = {
  EQUALS: { label: 'equals', takesValue: true },
  NOT_EQUALS: { label: 'does not equal', takesValue: true },
  IN: { label: 'is one of', takesValue: true, list: true },
  NOT_IN: { label: 'is not one of', takesValue: true, list: true },
  GT: { label: 'is greater than', takesValue: true, numeric: true },
  GTE: { label: 'is at least', takesValue: true, numeric: true },
  LT: { label: 'is less than', takesValue: true, numeric: true },
  LTE: { label: 'is at most', takesValue: true, numeric: true },
  EXISTS: { label: 'is present', takesValue: false },
  TRUTHY: { label: 'is true / non-empty', takesValue: false },
  CHANGED: { label: 'changed', takesValue: false },
};

const behaviourOf = (code?: string): OperatorBehaviour =>
  BEHAVIOUR[code || ''] || { label: code || '', takesValue: true };

/** Two worked examples, so the shape is shown rather than described. */
const EXAMPLES: { name: string; blurb: string; tree: ConditionTree }[] = [
  {
    name: 'Only high-priority, still open',
    blurb: 'Both must hold — ALL. The everyday shape: narrow a broad trigger down to the cases worth interrupting somebody for.',
    tree: {
      match: 'ALL',
      conditions: [
        { field: 'priority', operator: 'EQUALS', value: 'HIGH' },
        { field: 'status', operator: 'NOT_EQUALS', value: 'CLOSED' },
      ],
    },
  },
  {
    name: 'Escalated, or overdue by more than 48h',
    blurb: 'Either is enough — ANY. Note GT compares numbers, so the trigger must supply hoursOpen as a number, not "48".',
    tree: {
      match: 'ANY',
      conditions: [
        { field: 'status', operator: 'IN', value: ['ESCALATED', 'REOPENED'] },
        { field: 'hoursOpen', operator: 'GT', value: 48 },
      ],
    },
  },
];

const BLANK: ConditionNode = { field: '', operator: 'EQUALS', value: '' };

/** `"a, b"` becomes `["a","b"]` for the list operators; numbers stay numbers. */
const parseValue = (raw: string, operator: string): any => {
  const spec = behaviourOf(operator);
  if (!spec.takesValue) return undefined;
  if (spec.list) {
    return raw.split(',').map((v) => v.trim()).filter(Boolean);
  }
  if (spec.numeric) {
    const n = Number(raw);
    return raw.trim() !== '' && !Number.isNaN(n) ? n : raw;
  }
  return raw;
};

const displayValue = (v: any): string => {
  if (v === undefined || v === null) return '';
  return Array.isArray(v) ? v.join(', ') : String(v);
};

const ConditionTreeEditor = ({
  value, onChange, parameterNames = [], testId = 'ConditionTreeEditor',
}: ConditionTreeEditorProps) => {
  const [showJson, setShowJson] = useState(false);

  const { data: operatorVS } = useQuery({
    queryKey: ['vs', 'conditionOperator'],
    queryFn: () => valueSetsService.getByKey('conditionOperator', 'en'),
    staleTime: 3e5,
  });
  const { data: matchVS } = useQuery({
    queryKey: ['vs', 'conditionMatch'],
    queryFn: () => valueSetsService.getByKey('conditionMatch', 'en'),
    staleTime: 3e5,
  });

  // The value set decides which operators exist; the local table only says how
  // each one's input behaves. Labelled from the table where known, because
  // "does not equal" reads better than NOT_EQUALS to whoever is configuring it.
  const operatorOptions = ((operatorVS?.items || []) as any[]).map((i) => ({
    value: i.code,
    label: BEHAVIOUR[i.code]?.label || i.label || i.code,
  }));

  const matchOptions = ((matchVS?.items || []) as any[]).map((i) => ({
    value: i.code,
    label: i.code === 'ALL' ? 'ALL of these' : i.code === 'ANY' ? 'ANY of these' : i.label || i.code,
  }));

  const tree = value && value.conditions ? value : null;
  const conditions = tree?.conditions || [];

  const setTree = (next: ConditionTree) =>
    onChange(next.conditions.length ? next : null);

  const update = (index: number, next: ConditionNode) =>
    setTree({
      match: tree?.match || 'ALL',
      conditions: conditions.map((c, i) => (i === index ? next : c)),
    });

  const fieldOptions = parameterNames.map((n) => ({ value: n, label: n }));

  if (!tree) {
    return (
      <div className="flex flex-col gap-2" data-testid={testId}>
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Condition</label>
          <span className="block text-xs text-gray-400">
            When this notification fires. With none, it fires every time it is triggered.
          </span>
        </div>
        <Message severity="info" className="w-full" text="Fires every time — no condition set." />
        <div className="flex gap-2 flex-wrap">
          <Button type="button" label="Add a condition" icon="pi pi-plus" size="small"
            severity="secondary" outlined data-testid={`${testId}.Add`}
            onClick={() => setTree({ match: 'ALL', conditions: [{ ...BLANK }] })} />
          {EXAMPLES.map((ex) => (
            <Button key={ex.name} type="button" label={`Use: ${ex.name}`} icon="pi pi-bolt" size="small"
              severity="secondary" text onClick={() => setTree(ex.tree)} />
          ))}
        </div>
        <details className="text-xs text-ink-muted">
          <summary className="cursor-pointer">Two worked examples</summary>
          <div className="flex flex-col gap-3 mt-2">
            {EXAMPLES.map((ex) => (
              <div key={ex.name}>
                <div className="font-medium">{ex.name}</div>
                <div className="mb-1">{ex.blurb}</div>
                <pre className="bg-canvas dark:bg-gray-900 rounded p-2 overflow-x-auto text-xs">
                  {JSON.stringify(ex.tree, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        </details>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <div>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Condition</label>
        <span className="block text-xs text-gray-400">
          Fires only when these hold. A field must be one this notification declares as a
          parameter, or nothing supplies it at send time.
        </span>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600 dark:text-gray-300">Match</span>
        <Dropdown
          value={tree.match}
          options={matchOptions}
          optionLabel="label" optionValue="value" className="w-44"
          data-testid={`${testId}.Match`}
          onChange={(e) => setTree({ ...tree, match: e.value })}
        />
      </div>

      {conditions.map((condition, index) => {
        const spec = behaviourOf(condition.operator || 'EQUALS');
        return (
          <div key={index} className="flex items-center gap-2 flex-wrap">
            {fieldOptions.length ? (
              <Dropdown
                value={condition.field || null} options={fieldOptions}
                optionLabel="label" optionValue="value" filter editable
                placeholder="Field" className="w-56"
                data-testid={`${testId}.Field.${index}`}
                onChange={(e) => update(index, { ...condition, field: e.value })}
              />
            ) : (
              // No parameters declared yet — typing is the only option, and the
              // save will say so if the field is not declared by then.
              <InputText
                value={condition.field || ''} placeholder="Field (declare a parameter first)"
                className="w-56 font-mono text-xs" data-testid={`${testId}.Field.${index}`}
                onChange={(e) => update(index, { ...condition, field: e.target.value })}
              />
            )}

            <Dropdown
              value={condition.operator || 'EQUALS'} options={operatorOptions}
              optionLabel="label" optionValue="value" className="w-48"
              data-testid={`${testId}.Operator.${index}`}
              onChange={(e) => update(index, {
                ...condition,
                operator: e.value,
                // A value carried onto an operator that takes none would be
                // stored and never read.
                value: behaviourOf(e.value).takesValue
                  ? parseValue(displayValue(condition.value), e.value)
                  : undefined,
              })}
            />

            {spec.takesValue ? (
              <InputText
                value={displayValue(condition.value)}
                placeholder={spec.list ? 'comma, separated, values' : spec.numeric ? 'a number' : 'value'}
                className="flex-1 min-w-[10rem]"
                data-testid={`${testId}.Value.${index}`}
                onChange={(e) => update(index, {
                  ...condition,
                  value: parseValue(e.target.value, condition.operator || 'EQUALS'),
                })}
              />
            ) : (
              <span className="flex-1 text-xs text-gray-400">
                {condition.operator === 'CHANGED'
                  ? 'Needs a trigger that sends a before-image; without one this reads as "did not change".'
                  : 'Takes no value.'}
              </span>
            )}

            <button aria-label="Remove" type="button" title="Remove"
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => setTree({
                match: tree.match,
                conditions: conditions.filter((_, i) => i !== index),
              })}>
              <i className="pi pi-trash text-red-600 dark:text-red-400" />
            </button>
          </div>
        );
      })}

      <div className="flex gap-2 flex-wrap items-center">
        <Button type="button" label="Add condition" icon="pi pi-plus" size="small"
          severity="secondary" outlined data-testid={`${testId}.Add`}
          onClick={() => setTree({ match: tree.match, conditions: [...conditions, { ...BLANK }] })} />
        <Button type="button" label="Clear — fire every time" icon="pi pi-times" size="small"
          severity="secondary" text onClick={() => onChange(null)} />
        <Button type="button" label={showJson ? 'Hide JSON' : 'Show JSON'} icon="pi pi-code" size="small"
          severity="secondary" text onClick={() => setShowJson(!showJson)} />
      </div>

      {showJson && (
        <pre className="bg-canvas dark:bg-gray-900 rounded p-2 overflow-x-auto text-xs"
          data-testid={`${testId}.Json`}>
          {JSON.stringify(tree, null, 2)}
        </pre>
      )}
    </div>
  );
};

export default ConditionTreeEditor;
